import React, { FC, memo, useMemo, useEffect } from "react";

// DLS
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// constants
import { BR_TAG, BLACK_MEMBERSHIP_BENEFIT_BORDER } from "@payment-ui/constants";
import { replaceText } from "@swiggy-private/core-ui";
import { TICK_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import { BorderBottom, FirstBorderBottom, BlackBenefitsContainer, BlackBenefitText } from "../styles";
import styles from "./BlackMembershipBenefits.scss";

interface BlackMembershipBenefitsProps {
    successMessageInfoV2?: string;
    setBlackBenefitsTimeout: React.Dispatch<React.SetStateAction<number>>;
    isBolt?: boolean;
}

interface TextNodeProps {
    message: string;
    index: number;
    length: number;
    isOnlyOneLine: boolean;
    isBolt?: boolean;
}

const TextNode = ({ message, index, length, isOnlyOneLine, isBolt }: TextNodeProps) => {
    const formattedText = useMemo(() => replaceText(message, isBolt ? "" : styles.benefitImage), [isBolt, message]);
    const isLast = useMemo(() => index === length - 1, [index, length]);

    return (
        <>
            <BlackBenefitText
                variant="V2.overline.neutral.bold"
                color={isBolt ? "Dineout_Primary" : "secondary_One_Blck"}
                index={index}
            >
                {formattedText}
            </BlackBenefitText>

            {isOnlyOneLine && !isBolt && <FirstBorderBottom index={index} />}

            {!isLast && !isBolt && (
                <BorderBottom index={index}>
                    <Image cloudinaryId={BLACK_MEMBERSHIP_BENEFIT_BORDER} alt={"border-bottom"} />
                </BorderBottom>
            )}
        </>
    );
};

const BlackMembershipBenefits: FC<BlackMembershipBenefitsProps> = ({
    successMessageInfoV2,
    setBlackBenefitsTimeout,
    isBolt,
}) => {
    const messages = useMemo(() => {
        if (!successMessageInfoV2) {
            return [];
        }

        if (successMessageInfoV2.includes(BR_TAG)) {
            return successMessageInfoV2.split(BR_TAG);
        }

        return [successMessageInfoV2];
    }, [successMessageInfoV2]);

    useEffect(() => {
        setBlackBenefitsTimeout(messages.length);
    }, [setBlackBenefitsTimeout, messages.length]);

    const isOnlyOneLine = useMemo(() => messages.length === 1, [messages]);

    return (
        <BlackBenefitsContainer
            data-testid={TICK_SCREEN_TEST_IDS.BLACK_MEMBERSHIP_BENEFITS}
            isOnlyOneLine={isOnlyOneLine}
            isBolt={isBolt}
        >
            {messages.length > 0 &&
                messages.map((message: string, index: number) => (
                    <TextNode
                        key={message}
                        message={message}
                        index={index}
                        length={messages?.length}
                        isOnlyOneLine={isOnlyOneLine}
                        isBolt={isBolt}
                    />
                ))}
        </BlackBenefitsContainer>
    );
};

export default memo(BlackMembershipBenefits);
